/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import CommonAPI from '@fusioncharts/charts/src/chart/_internal/commonchartapi';
import arrayHasContent from '@fusioncharts/utils/src/type/array-has-content';
import isObject from '@fusioncharts/utils/src/type/is-object';
import legendItemFactory from '@fusioncharts/charts/src/factories/chord-legend';
import nodelinkManagerFactor from '../../factories/node-link-manager';
import LinearScale from '@fusioncharts/utils/src/scales/linear';
import {
  parseUnsafeString,
  pluck,
  convertColor,
  getDarkColor,
  pluckNumber,
  UNDEF,
  NORMAL,
  pluckFontSize,
  BOLD,
  hashify,
  BLANKSTRING
} from '@fusioncharts/core/src/lib';
import equal from '@fusioncharts/utils/src/string/equal';
import safeMax from '@fusioncharts/utils/src/array/safe-max';
import NodeLinkManager from '../../dataset/chord/node-link-manager';
import { _manageLegendSpace } from '@fusioncharts/charts/src/chart/_internal/legend-spacemanager';

const CHART_STR = 'Chord',
  POST = 'post',
  PRE = 'pre',
  DEFAULT_PALETTE_COLOR = [
    '1E77B4',
    'FF7F0E',
    '2BA02C',
    'D62728',
    '9466BD',
    '8C564B',
    'E376C2',
    '7F7F7F',
    'BCBD22',
    '17BECF'
  ],
  DEACTIVE_COLOR = '#c4c4c4',
  DEFAULT_BG_COLOR = 'FFFFFF,FFFFFF',
  TANGENTIAL = 'tangential',
  OUTSIDE = 'outside',
  INSIDE = 'inside',
  DEF_FONT_FAMILY = 'Verdana,sans',
  ITALIC = 'italic',
  NODE_LABEL_COLOR = '#5F5F5F',
  LABEL_POSITIONS = [TANGENTIAL, OUTSIDE, INSIDE],
  isValidLink = obj => isObject(obj) &&
    parseUnsafeString(obj.from) &&
    parseUnsafeString(obj.to),
  isValidNode = (obj, nodeMap) => isObject(obj) && parseUnsafeString(obj.label) && nodeMap[obj.label];

/**
 * Creates radar class
 * @class
 */
class Chord extends CommonAPI {
  constructor (id) {
    super(id);
    const chart = this;
    chart.deregisterFactory('canvas');
    chart.registerFactory('legend', legendItemFactory);
    chart.registerFactory('nodelinkManager', nodelinkManagerFactor);
    chart.defaultPaletteOptions = {
      // Store colors now
      paletteColors:
        [
          DEFAULT_PALETTE_COLOR,
          DEFAULT_PALETTE_COLOR,
          DEFAULT_PALETTE_COLOR,
          DEFAULT_PALETTE_COLOR,
          DEFAULT_PALETTE_COLOR
        ],
      bgColor: [
        DEFAULT_BG_COLOR,
        DEFAULT_BG_COLOR,
        DEFAULT_BG_COLOR,
        DEFAULT_BG_COLOR,
        DEFAULT_BG_COLOR
      ]
    };
  }

  /**
   * Sets the default configuration
   */
  __setDefaultConfig () {
    super.__setDefaultConfig();
    const config = this.config;
    // for skipping drawing of canvas
    config.skipCanvasDrawing = true;
    config.friendlyName = CHART_STR;
    config.nodes = {};
    config.mode = POST;
    config.isPost = true;
    config.nodeSpacing = 2;
    config.minNodeSize = 0.01;
    config.startingAngle = 0;
    config.links = {};
    config.linksOrder = [];
    config.nodesOrder = [];
    config.totalAngle = 360;
    config.clockwise = 1;
    config.nodeLabelGap = 10;
    config.nodeLabelPosition = 10;
    config.nodeThickness = 12;
    // config.total = 0;
    config.nodeLinkPadding = 3;
    config.linkAlpha = 70;
    config.linkBorderAlpha = 100;
    config.linkBorderThickness = 1;
    config.nodeHoverAlpha = 100;
    config.linkHoverAlpha = 100;
    config.nodeHoverColor = UNDEF;
    config.linkHoverColor = UNDEF;
    config.showNodeLabels = 1;
    config.showNodeBorder = 1;
    config.showLinkBorder = 1;
    config.nodeBorderColor = UNDEF;
    config.nodeBorderThickness = 1;
    config.nodeBorderDashed = 0;
    config.nodeBorderDashedLen = 2;
    config.nodeBorderDashedGap = 1;
    config.nodeBorderAlpha = 100;
    config.showLinkValueOnHover = 1;
    config.nodeAlpha = 70;
    config.nodeLabelPosition = TANGENTIAL;
    config.nodeLabelFont = DEF_FONT_FAMILY;
    config.nodeLabelFontSize = 12;
    config.nodeLabelFontBold = NORMAL;
    config.nodeLabelFontItalic = NORMAL;
    config.highlightEffect = 1;
    config.enableToggle = 1;
    config.chordradius = 100;
    config.nodeLabelColor = NODE_LABEL_COLOR;
    config.unfocussedAlpha = 35;
    config.deactiveNodeColor = DEACTIVE_COLOR;
    config.sortOrder = UNDEF;
    config.tooltipsepchar = BLANKSTRING;
    config.linkColorByDominance = 1;
    config.useEllipsesOnOverflow = 1;
  }

  configureAttributes (dataObj) {
    super.configureAttributes(dataObj);
    let chart = this,
      config = chart.config,
      { nodes, links } = config,
      nodesArr = config.nodesOrder,
      linksArr = config.linksOrder,
      connectors = config.connectors = dataObj.links,
      len = connectors.length,
      numberFormatter = chart.getFromEnv('number-formatter'),
      chartAttrs = dataObj.chart,
      colorM = chart.getFromEnv('color-manager'),
      i;

    config.mode = chartAttrs.mode === PRE ? PRE : POST;
    config.isPost = equal(config.mode, POST);

    // Parse chart attributes
    config.minNodeSize = pluckNumber(chartAttrs.minnodesize, config.minNodeSize);
    config.startingAngle = pluckNumber(chartAttrs.startingangle, config.startingAngle);
    config.totalAngle = pluckNumber(chartAttrs.totalangle, config.totalAngle);
    config.nodeLabelGap = pluckNumber(chartAttrs.nodelabelpadding, config.nodeLabelGap);
    config.nodeThickness = pluckNumber(chartAttrs.nodethickness, config.nodeThickness);
    config.nodeSpacing = pluckNumber(chartAttrs.nodespacing, config.nodeSpacing);
    config.clockwise = pluckNumber(chartAttrs.clockwise, config.clockwise);
    config.nodeLinkPadding = pluckNumber(chartAttrs.nodelinkpadding, config.nodeLinkPadding);
    config.linkAlpha = pluckNumber(chartAttrs.linkalpha, config.linkAlpha);
    config.linkBorderAlpha = pluckNumber(chartAttrs.linkborderalpha, config.linkBorderAlpha);
    config.linkBorderThickness = pluckNumber(chartAttrs.linkborderthickness, config.linkBorderThickness);
    config.nodeHoverAlpha = pluckNumber(chartAttrs.nodehoveralpha, config.nodeHoverAlpha);
    config.linkHoverAlpha = pluckNumber(chartAttrs.linkhoveralpha, config.linkHoverAlpha);
    config.showLinkValueOnHover = pluckNumber(chartAttrs.showlinkvalueonhover, config.showLinkValueOnHover);
    // config.nodeHoverColor = pluck(chartAttrs.nodehovercolor, config.nodeHoverColor);
    // config.linkHoverColor = pluck(chartAttrs.linkhovercolor, config.linkHoverColor);
    config.showNodeLabels = pluckNumber(chartAttrs.shownodelabels, config.showNodeLabels);
    config.showNodeBorder = pluckNumber(chartAttrs.shownodeborder, config.showNodeBorder);
    config.showLinkBorder = pluckNumber(chartAttrs.showlinkborder, config.showLinkBorder);
    config.nodeBorderColor = pluck(chartAttrs.nodebordercolor, config.nodeBorderColor);
    config.nodeBorderThickness = pluckNumber(chartAttrs.nodeborderthickness, config.nodeBorderThickness);
    config.nodeBorderDashed = pluckNumber(chartAttrs.nodeborderdashed, config.nodeBorderDashed);
    config.nodeBorderDashedLen = pluckNumber(chartAttrs.nodeborderdashedlen, config.nodeBorderDashedLen);
    config.nodeBorderDashedGap = pluckNumber(chartAttrs.nodeborderdashedgap, config.nodeBorderDashedGap);
    config.nodeBorderAlpha = pluckNumber(chartAttrs.nodeborderalpha, config.nodeBorderAlpha);
    config.nodeAlpha = pluckNumber(chartAttrs.nodealpha, config.nodeAlpha);
    config.nodeLabelPosition = LABEL_POSITIONS.includes(chartAttrs.nodelabelposition) ? chartAttrs.nodelabelposition : config.nodeLabelPosition;
    config.nodeLabelFont = pluck(chartAttrs.nodelabelfont, chartAttrs.basefont, config.nodeLabelFont);
    config.nodeLabelFontSize = pluckFontSize(chartAttrs.nodelabelfontsize,
      this.computeFontSize(chartAttrs.basefontsize), config.nodeLabelFontSize);
    config.nodeLabelFontBold = pluckNumber(chartAttrs.nodelabelfontbold, 0) ? BOLD : NORMAL;
    config.nodeLabelColor = pluck(chartAttrs.nodelabelcolor, chartAttrs.basefontcolor, config.nodeLabelColor);
    config.nodeLabelFontItalic = pluckNumber(chartAttrs.nodelabelfontitalic, 0) ? ITALIC : NORMAL;
    config.highlightEffect = pluckNumber(chartAttrs.highlighteffect, config.highlightEffect);
    config.enableToggle = pluckNumber(chartAttrs.enabletoggle, config.enableToggle);
    config.chordradius = Math.max(pluckNumber(chartAttrs.chordradius, config.chordradius), 0);
    config.unfocussedAlpha = pluckNumber(chartAttrs.unfocussedalpha, config.unfocussedAlpha);
    config.deactiveNodeColor = pluck(chartAttrs.deactivenodecolor, config.deactiveNodeColor);
    config.sortOrder = pluck(chartAttrs.sortorder, config.sortOrder);
    config.linkColorByDominance = pluckNumber(chartAttrs.linkcolorbydominance, config.linkColorByDominance);
    config.textOutline = pluckNumber(chartAttrs.textoutline, 0);
    config.useEllipsesOnOverflow = pluckNumber(chartAttrs.useellipsesonoverflow, config.useEllipsesOnOverflow);
    config.datalabelStyle = {
      'font-size': config.nodeLabelFontSize,
      'font-family': config.nodeLabelFont,
      'font-weight': config.nodeLabelFontBold,
      'font-style': config.nodeLabelFontItalic
    };

    // when labels are kept inside the there is no node label gap
    if (config.nodeLabelPosition === INSIDE) {
      config.nodeLabelGap = 0;
    }

    // creating valid nodes link
    for (i = 0; i < len; ++i) {
      if (isValidLink(connectors[i])) {
        let connector = connectors[i],
          { from, to, value, tooltext } = connector,
          sanitisedValue = safeMax([numberFormatter.getCleanValue(value), 0], Number),
          key,
          link;
        from = parseUnsafeString(from);
        to = parseUnsafeString(to);
        key = [from, to].sort().toString();
        if (!nodes[from]) {
          nodes[from] = {
            index: nodesArr.push(from) - 1,
            total: 0,
            adjustedTotal: 0,
            nodeCovered: 0,
            label: from,
            linkedLinks: [],
            scale: new LinearScale(),
            active: true,
            nodeLabelGap: config.nodeLabelGap,
            unfocussed: false,
            hovered: false,
            toolTipSepChar: config.tooltipsepchar,
            showToolTip: config.showToolTip
          };
        }
        if (!nodes[to]) {
          nodes[to] = {
            index: nodesArr.push(to) - 1,
            total: 0,
            adjustedTotal: 0,
            nodeCovered: 0,
            label: to,
            linkedLinks: [],
            scale: new LinearScale(),
            active: true,
            nodeLabelGap: config.nodeLabelGap,
            unfocussed: false,
            hovered: false,
            toolTipSepChar: config.tooltipsepchar,
            showToolTip: config.showToolTip
          };
        }
        if (!links[key]) {
          link = links[key] = {
            key,
            points: [],
            dominantNode: [],
            subservientNode: [],
            subservientFlowDataValue: [],
            dominantFlowDataValue: [],
            dominantNodeColor: [],
            subservientNodeColor: [],
            index: linksArr.push(key) - 1,
            linkedNodes: config.isPost ? [to, from] : [from, to],
            tooltip: {},
            visible: true,
            tooltext: tooltext,
            unfocussed: false,
            hovered: false,
            focussedState: {},
            unfocussedState: {},
            normalState: {},
            sanitisedValue: 0,
            toolTipSepChar: config.tooltipsepchar,
            showToolTip: config.showToolTip
          };
          nodes[from].linkedLinks.push(key);
          nodes[to].linkedLinks.push(key);
          link.alpha = pluckNumber(connector.alpha, config.linkAlpha);
          link.hoverAlpha = pluckNumber(connector.hoveralpha, config.linkHoverAlpha);
          link.borderAlpha = pluckNumber(connector.borderalpha, config.linkBorderAlpha);
          link.tooltext = pluck(connector.tooltext, config.linktooltext, UNDEF);
          link.showBorder = config.showLinkBorder;
          link.borderThickness = config.linkBorderThickness;
          link.unfocussedAlpha = pluckNumber(connector.unfocussedAlpha, config.unfocussedAlpha);
          link.style = config.datalabelStyle;
        } else {
          link = links[key];
        }

        nodes[config.isPost ? to : from].total += sanitisedValue;
        // config.total += sanitisedValue;
        link.tooltip[`${from} to ${to}`] = {
          value: numberFormatter.dataLabels(sanitisedValue)
        };
        link.showLinkValueOnHover = config.showLinkValueOnHover;
        link[config.isPost ? to : from] = sanitisedValue;
        links[key].sanitisedValue += sanitisedValue;
      }
    }

    for (const key in links) {
      if (links.hasOwnProperty(key) && links[key].sanitisedValue === 0) {
        delete links[key];
        config.linksOrder = linksArr = linksArr.filter(link => link !== key);
        key.split(',').forEach(label => {
          nodes[label].linkedLinks =
            nodes[label].linkedLinks.filter(linkedLink => linkedLink !== key);
        });
      }
    }

    // copying all the node level configurations in valid nodes
    if (arrayHasContent(dataObj.nodes)) {
      dataObj.nodes.forEach(node => {
        if (isValidNode(node, nodes)) {
          let label = parseUnsafeString(node.label);
          nodes[label] = Object.assign({}, node, nodes[label]);
        }
      });
    }

    // require good name and value
    if (config.sortOrder === 'ascending') {
      nodesArr.sort((key1, key2) => nodes[key1].total - nodes[key2].total);
    } else if (config.sortOrder === 'descending') {
      nodesArr.sort((key1, key2) => nodes[key2].total - nodes[key1].total);
    }

    // Parse node cosmetics
    nodesArr.forEach((label, index) => {
      let node = nodes[label],
        resolvedStrokeWidth = (config.showNodeBorder ? config.nodeBorderThickness : 0) || 0;
      node.index = index;
      node.label = parseUnsafeString(node.label);
      node.alpha = pluckNumber(node.alpha, config.nodeAlpha);
      node.borderAlpha = pluckNumber(node.borderalpha, config.nodeBorderAlpha);
      node.hoverAlpha = pluckNumber(node.hoveralpha, config.nodeHoverAlpha);
      node.borderDashedLen = pluckNumber(node.borderdashedlen, config.nodeBorderDashedLen);
      node.borderDashedGap = pluckNumber(node.borderdashedgap, config.nodeBorderDashedGap);
      node.borderDashed = pluckNumber(node.borderdashed, config.nodeBorderDashed) ? [node.borderDashedLen, node.borderDashedGap] : 'none';

      node.rawColor = pluck(node.color, colorM.getPlotColor(index));
      node.unfocussedAlpha = pluckNumber(node.unfocussedalpha, config.unfocussedAlpha);
      node.borderColor = pluck(node.bordercolor, config.nodeBorderColor, getDarkColor(node.rawColor, 80));
      node.tooltext = pluck(node.tooltext, chartAttrs.plottooltext, UNDEF);

      node.color = hashify(node.rawColor);
      node.labelPosition = pluck(node.labelposition, config.nodeLabelPosition);
      node.nodeLabelGap = config.nodeLabelGap;
      // Export error when textPaths are used
      if (pluckNumber(chartAttrs.exportenabled) && (node.labelPosition === OUTSIDE || node.labelPosition === INSIDE)) {
        node.showLabel = 0;
      } else {
        node.showLabel = pluckNumber(node.showlabel, config.showNodeLabels);
      }
      node.showBorder = config.showNodeBorder;
      node.labelColor = pluck(node.labelcolor, config.nodeLabelColor);
      node.style = config.datalabelStyle;

      node.focussedState = {
        fill: convertColor(node.rawColor, node.hoverAlpha),
        stroke: convertColor(node.borderColor, node.borderAlpha),
        'stroke-width': resolvedStrokeWidth,
        'stroke-dasharray': node.borderDashed
      };
      node.normalState = {
        fill: convertColor(node.rawColor, node.alpha),
        stroke: convertColor(node.borderColor, node.borderAlpha),
        'stroke-width': resolvedStrokeWidth,
        'stroke-dasharray': node.borderDashed
      };
      node.unfocussedState = {
        fill: convertColor(node.rawColor, node.unfocussedAlpha),
        stroke: convertColor(node.borderColor, node.unfocussedAlpha),
        'stroke-width': resolvedStrokeWidth,
        'stroke-dasharray': node.borderDashed
      };
      node.deactiveState = {
        fill: config.deactiveNodeColor,
        stroke: config.deactiveNodeColor,
        'stroke-width': resolvedStrokeWidth,
        'stroke-dasharray': node.borderDashed
      };
      node.linkedLinks.forEach(key => {
        const startsWithRegex = new RegExp(`^${node.label}`),
          tooltip = links[key].tooltip;
        for (let toolKey in tooltip) {
          if (tooltip.hasOwnProperty(toolKey) && startsWithRegex.test(toolKey)) {
            tooltip[toolKey].color = node.color;
          }
        }
      });
    });
    linksArr.forEach(key => {
      let link = links[key],
        [from, to] = link.linkedNodes;

      if (from === to) {
        link.dominantNode.push(from);
        link.subservientNode.push(from);
        link.subservientFlowDataValue.push(numberFormatter.dataLabels(link[from]));
        link.dominantFlowDataValue.push(numberFormatter.dataLabels(link[from]));
        link.dominantNodeColor.push(nodes[from].color);
        link.subservientNodeColor.push(nodes[from].color);
        // for equal contributing nodes
      } if (link[to] === link[from]) {
        link.dominantNode.push(from, to);
        link.subservientNode.push(to, from);
        link.subservientFlowDataValue.push(numberFormatter.dataLabels(link[to]));
        link.dominantFlowDataValue.push(numberFormatter.dataLabels(link[from]));
        link.dominantNodeColor.push(nodes[from].color, nodes[to].color);
        link.subservientNodeColor.push(nodes[to].color, nodes[from].color);
      } else {
        if (config.isPost) {
          if ((link[to] || 0) > (link[from] || 0)) {
            config.linkColorByDominance ? link.dominantNode.push(to) : link.dominantNode.push(from);
            config.linkColorByDominance ? link.subservientNode.push(from) : link.subservientNode.push(to);
            config.linkColorByDominance ? link.dominantFlowDataValue.push(numberFormatter.dataLabels(link[to])) : link.dominantFlowDataValue.push(numberFormatter.dataLabels(link[from]));
            config.linkColorByDominance ? link.subservientFlowDataValue.push(numberFormatter.dataLabels(link[from])) : link.subservientFlowDataValue.push(numberFormatter.dataLabels(link[to]));
            config.linkColorByDominance ? link.dominantNodeColor.push(nodes[to].color) : link.dominantNodeColor.push(nodes[from].color);
            config.linkColorByDominance ? link.subservientNodeColor.push(nodes[from].color) : link.subservientNodeColor.push(nodes[to].color);
          } else {
            config.linkColorByDominance ? link.dominantNode.push(from) : link.dominantNode.push(to);
            config.linkColorByDominance ? link.subservientNode.push(to) : link.subservientNode.push(from);
            config.linkColorByDominance ? link.dominantFlowDataValue.push(numberFormatter.dataLabels(link[from])) : link.dominantFlowDataValue.push(numberFormatter.dataLabels(link[to]));
            config.linkColorByDominance ? link.subservientFlowDataValue.push(numberFormatter.dataLabels(link[to])) : link.subservientFlowDataValue.push(numberFormatter.dataLabels(link[from]));
            config.linkColorByDominance ? link.dominantNodeColor.push(nodes[from].color) : link.dominantNodeColor.push(nodes[to].color);
            config.linkColorByDominance ? link.subservientNodeColor.push(nodes[to].color) : link.subservientNodeColor.push(nodes[from].color);
          }
        } else {
          if ((link[to] || 0) > (link[from] || 0)) {
            config.linkColorByDominance ? link.dominantNode.push(from) : link.dominantNode.push(to);
            config.linkColorByDominance ? link.subservientNode.push(to) : link.subservientNode.push(from);
            config.linkColorByDominance ? link.dominantFlowDataValue.push(numberFormatter.dataLabels(link[from])) : link.dominantFlowDataValue.push(numberFormatter.dataLabels(link[to]));
            config.linkColorByDominance ? link.subservientFlowDataValue.push(numberFormatter.dataLabels(link[to])) : link.subservientFlowDataValue.push(numberFormatter.dataLabels(link[from]));
            config.linkColorByDominance ? link.dominantNodeColor.push(nodes[from].color) : link.dominantNodeColor.push(nodes[to].color);
            config.linkColorByDominance ? link.subservientNodeColor.push(nodes[to].color) : link.subservientNodeColor.push(nodes[from].color);
          } else {
            config.linkColorByDominance ? link.dominantNode.push(to) : link.dominantNode.push(from);
            config.linkColorByDominance ? link.subservientNode.push(from) : link.subservientNode.push(to);
            config.linkColorByDominance ? link.dominantFlowDataValue.push(numberFormatter.dataLabels(link[to])) : link.dominantFlowDataValue.push(numberFormatter.dataLabels(link[from]));
            config.linkColorByDominance ? link.subservientFlowDataValue.push(numberFormatter.dataLabels(link[from])) : link.subservientFlowDataValue.push(numberFormatter.dataLabels(link[to]));
            config.linkColorByDominance ? link.dominantNodeColor.push(nodes[to].color) : link.dominantNodeColor.push(nodes[from].color);
            config.linkColorByDominance ? link.subservientNodeColor.push(nodes[from].color) : link.subservientNodeColor.push(nodes[to].color);
          }
        }
      }
    });
  }

  /**
   * Sets the name of the component
   * @return {string} name
   */
  static getName () {
    return 'Chord';
  }

  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'Chord';
  }

  /**
   * This method return the dataset definations for this charts
   * @return {string} dataset defination
   */
  getDSdef () {
    return NodeLinkManager;
  }

  /**
   * function to calculate and allote space for legend
   */
  _manageLegendSpace () {
    _manageLegendSpace.call(this);
  }

  _spaceManager () {
    let chart = this,
      config = chart.config,
      height,
      width,
      cx,
      cy,
      nodesLinkManager = chart.getChildren('node-link-manager')[0],
      maxNodeDim,
      maxRadius,
      minRadius,
      maxSpace;

    config.showLegend && chart._manageLegendSpace();
    chart._manageChartMenuBar(config.availableHeight * 0.6);
    chart.allocateDimensionOfChartMenuBar();
    chart.config.showLegend && chart.getChildren('legend') && chart.getChildren('legend')[0].postSpaceManager();

    height = config.canvasHeight;
    width = config.canvasWidth;
    cx = config.canvasLeft + width / 2;
    cy = config.canvasTop + height / 2;
    maxRadius = Math.min(height, width) / 2;
    minRadius = maxRadius * 0.3;
    maxSpace = maxRadius - minRadius - config.nodeLabelGap;

    maxNodeDim = nodesLinkManager._manageSpace({
      maxSpace
    });

    chart.config.cx = cx;
    chart.config.cy = cy;

    const nodeOuterRadius = Math.max((maxRadius - maxNodeDim.width - config.nodeLabelGap) * config.chordradius / 100, minRadius),
      nodeInnerRadius = nodeOuterRadius - config.nodeThickness,
      ribbonRadius = nodeInnerRadius - config.nodeLinkPadding;

    config.ribbonRadius = ribbonRadius;

    nodesLinkManager.setDimension({ nodeOuterRadius, nodeInnerRadius, ribbonRadius });
    nodesLinkManager.setTranslation(cx, cy);
  }
  /**
   * Function to check if the chart has the proper data or not
   * @return {boolean} if JSON data is valid or not
   */
  _checkInvalidData () {
    const chartInstance = this.getFromEnv('chartInstance');

    // Checking if the chart has proper data
    if (!arrayHasContent(this.getFromEnv('dataSource').links)) {
      chartInstance.__state.dataReady = false;
      chartInstance.jsVars.hasNativeMessage = true;
      chartInstance.jsVars.drawCount += 1;

      return true;
    }
    return !this;
  }

  _checkInvalidSpecificData () {
    // temp code
    return !this;
  }
}
export default Chord;
